
import React, { useState, useEffect } from 'react';
import { 
  getAllCommandMetrics, 
  getSummaryMetrics, 
  clearMetrics, 
  type CommandMetricsByCommand 
} from '@/utils/commandMetrics';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from 'recharts';
import { downloadAsCSV } from '@/utils/export';

interface SummaryMetrics {
  totalCommandsTracked: string;
  totalExecutions: string;
  successRate: string;
  topCommands: { command: string; uses: string; successRate: string }[];
  mostFailedCommands: { command: string; attempts: string; failureRate: string }[];
}

interface StatCardProps {
  title: string;
  value: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value }) => (
  <Card>
    <CardHeader>
      <CardTitle>{title}</CardTitle>
    </CardHeader>
    <CardContent>
      <div className="text-2xl font-bold">{value}</div>
    </CardContent>
  </Card>
);

interface CommandRowProps {
  command: string;
  success: number;
  failure: number;
  lastUsed?: number;
}

const CommandRow: React.FC<CommandRowProps> = ({ command, success, failure, lastUsed }) => (
  <TableRow>
    <TableCell className="font-medium">{command}</TableCell>
    <TableCell>{success}</TableCell>
    <TableCell>{failure}</TableCell>
    <TableCell>{(success / (success + failure) * 100).toFixed(1)}%</TableCell>
    <TableCell>{lastUsed ? new Date(lastUsed).toLocaleString() : 'Never'}</TableCell>
  </TableRow>
);

const CommandDebugger = () => {
  const [metrics, setMetrics] = useState<SummaryMetrics | null>(null);
  const [allCommands, setAllCommands] = useState<CommandMetricsByCommand | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCommands, setFilteredCommands] = useState<CommandMetricsByCommand | null>(null);
  const [pieChartData, setPieChartData] = useState<any[]>([]);
  const [barChartData, setBarChartData] = useState<any[]>([]);

  useEffect(() => {
    const summary = getSummaryMetrics();
    setMetrics(summary);

    const all = getAllCommandMetrics();
    setAllCommands(all);
    setFilteredCommands(all);
  }, []);

  useEffect(() => {
    if (allCommands) {
      const filtered = Object.fromEntries(
        Object.entries(allCommands).filter(([command]) =>
          command.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
      setFilteredCommands(filtered);
    }
  }, [searchTerm, allCommands]);

  useEffect(() => {
    if (metrics) {
      setPieChartData([
        { name: 'Success', value: parseFloat(metrics.successRate) },
        { name: 'Failure', value: 100 - parseFloat(metrics.successRate) },
      ]);

      setBarChartData(
        metrics.topCommands.map(cmd => ({
          name: cmd.command,
          uses: parseInt(cmd.uses),
        }))
      );
    }
  }, [metrics]);

  const handleClearMetrics = () => {
    clearMetrics();
    setAllCommands(null);
    setMetrics(null);
    setFilteredCommands(null);
    setPieChartData([]);
    setBarChartData([]);
  };

  // Helper function to export command metrics data to CSV
  const handleExportCSV = () => {
    const allMetrics = getAllCommandMetrics();
    
    // Create data for export
    const data = Object.entries(allMetrics).map(([command, stats]) => ({
      command,
      success_count: stats.success,
      failure_count: stats.failure,
      total_executions: stats.success + stats.failure,
      success_rate: ((stats.success / (stats.success + stats.failure)) * 100).toFixed(1) + '%',
      last_used: stats.lastUsed ? new Date(stats.lastUsed).toLocaleString() : 'Never'
    }));
    
    downloadAsCSV(data, 'voice_command_metrics');
  };

  return (
    <div className="p-6 container max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Voice Command Analytics</h1>
          <p className="text-muted-foreground mt-1">
            Monitor and debug voice command performance
          </p>
        </div>
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            onClick={handleExportCSV}
          >
            Export Data
          </Button>
          <Button 
            variant="destructive" 
            onClick={handleClearMetrics}
          >
            Clear Metrics
          </Button>
        </div>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="mb-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="commands">Commands</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="debug">Debug</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {metrics && (
              <>
                <div className="col-span-1 md:col-span-2">
                  <Card>
                    <CardHeader>
                      <CardTitle>Command Stats</CardTitle>
                      <CardDescription>Key metrics for voice commands</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <StatCard 
                          title="Commands Tracked" 
                          value={metrics.totalCommandsTracked} 
                        />
                        <StatCard 
                          title="Total Executions" 
                          value={metrics.totalExecutions} 
                        />
                        <StatCard 
                          title="Success Rate" 
                          value={metrics.successRate} 
                        />
                        <StatCard 
                          title="Failures" 
                          value={
                            (parseInt(metrics.totalExecutions) - 
                             (parseInt(metrics.totalExecutions) * 
                              parseInt(metrics.successRate) / 100)).toFixed(0)
                          } 
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="col-span-1 md:col-span-1">
                  <Card>
                    <CardHeader>
                      <CardTitle>Success Rate</CardTitle>
                      <CardDescription>Overall success vs failure rate</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={200}>
                        <PieChart>
                          <Pie
                            data={pieChartData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            fill="#8884d8"
                            label
                          >
                            {pieChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={index === 0 ? '#22c55e' : '#ef4444'} />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="commands">
          <Card>
            <CardHeader>
              <CardTitle>Command List</CardTitle>
              <CardDescription>All recorded voice commands</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4">
                <Label htmlFor="search">Search Commands</Label>
                <Input
                  type="text"
                  id="search"
                  placeholder="Enter command..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Command</TableHead>
                      <TableHead>Success</TableHead>
                      <TableHead>Failure</TableHead>
                      <TableHead>Success Rate</TableHead>
                      <TableHead>Last Used</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCommands &&
                      Object.entries(filteredCommands).map(([command, stats]) => (
                        <CommandRow
                          key={command}
                          command={command}
                          success={stats.success}
                          failure={stats.failure}
                          lastUsed={stats.lastUsed}
                        />
                      ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics">
          <Card>
            <CardHeader>
              <CardTitle>Top Commands</CardTitle>
              <CardDescription>Most frequently used voice commands</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={barChartData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="uses" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="debug">
          <Card>
            <CardHeader>
              <CardTitle>Debug Tools</CardTitle>
              <CardDescription>Tools for debugging voice command issues</CardDescription>
            </CardHeader>
            <CardContent>
              <p>Coming soon...</p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CommandDebugger;
